import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import Modal from '@amzn/awsui-components-react/polaris/modal';
import Box from '@amzn/awsui-components-react/polaris/box';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Button from '@amzn/awsui-components-react/polaris/button';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Textarea from '@amzn/awsui-components-react/polaris/textarea';
import Input from '@amzn/awsui-components-react/polaris/input';
import Select from '@amzn/awsui-components-react/polaris/select';
import Alert from '@amzn/awsui-components-react/polaris/alert';
import FeedbackService, { FeedbackData } from '../services/FeedbackService';
import { FeedbackResponse } from '../types/feedback';
import { useAuth } from '../contexts/AuthContext';

export interface FeedbackModalProps {
  onDismiss?: () => void;
}

export interface FeedbackModalRef {
  open: () => void;
}

const FeedbackModal = forwardRef<FeedbackModalRef, FeedbackModalProps>(
  function FeedbackModalComponent({ onDismiss }, ref) {
    const { username, email: userEmail } = useAuth();
    const [visible, setVisible] = useState(false);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [feedbackType, setFeedbackType] = useState<any>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<{
      type: 'success' | 'error';
      message: string;
    } | null>(null);

    // Debug: Log when component mounts
    useEffect(() => {
      console.log("FeedbackModal mounted");
    }, []);

    // Expose the open method to parent components
    useImperativeHandle(ref, function() {
      console.log("FeedbackModal useImperativeHandle called");
      return {
        open: function() {
          console.log("FeedbackModal open method called");
          setVisible(true);
          // Reset form when opening
          setSubmitStatus(null);
        }
      };
    });

    const handleDismiss = () => {
      console.log("FeedbackModal handleDismiss called");
      setVisible(false);
      if (onDismiss) {
        onDismiss();
      }
    };

    const handleSubmit = async () => {
      // Validate required fields
      if (!message) {
        setSubmitStatus({
          type: 'error',
          message: 'Please provide feedback in the message field'
        });
        return;
      }

      if (!feedbackType) {
        setSubmitStatus({
          type: 'error',
          message: 'Please select a feedback type'
        });
        return;
      }

      setIsSubmitting(true);
      setSubmitStatus(null);

      try {
        const feedbackData: FeedbackData = {
          name: username,
          email: userEmail,
          subject,
          message,
          feedbackType: feedbackType
        };

        const response: FeedbackResponse = await FeedbackService.submitFeedback(feedbackData);

        setSubmitStatus({
          type: 'success',
          message: response.message || 'Thank you for your feedback!'
        });

        // Reset form after successful submission
        setSubject('');
        setMessage('');
        setFeedbackType(null);

        // Close modal after a delay
        setTimeout(() => {
          handleDismiss();
        }, 2000);
      } catch (error) {
        console.error('Error submitting feedback:', error);
        const errorMsg = error instanceof Error ? error.message : 'Failed to submit feedback. Please try again.';
        setSubmitStatus({
          type: 'error',
          message: errorMsg
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    console.log("FeedbackModal rendering, visible:", visible);

    return (
      <Modal
        visible={visible}
        onDismiss={handleDismiss}
        header="Send Feedback"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleDismiss}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                Submit Feedback
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          {submitStatus && (
            <Alert type={submitStatus.type}>
              {submitStatus.message}
            </Alert>
          )}

          <FormField label="Name">
            <Input
              value={username}
              disabled={true}
              placeholder="Your name"
            />
          </FormField>

          <FormField label="Email">
            <Input
              value={userEmail}
              disabled={true}
              type="email"
              placeholder="Your email address"
            />
          </FormField>

          <FormField label="Feedback Type *" constraintText="Required">
            <Select
              selectedOption={feedbackType}
              onChange={({ detail }) => setFeedbackType(detail.selectedOption)}
              options={[
                { label: 'General Feedback', value: 'general' },
                { label: 'Bug Report', value: 'bug' },
                { label: 'Feature Request', value: 'feature' },
                { label: 'Question', value: 'question' }
              ]}
              placeholder="Select a feedback type"
            />
          </FormField>

          <FormField label="Subject">
            <Input
              value={subject}
              onChange={({ detail }) => setSubject(detail.value)}
              placeholder="Subject of your feedback"
            />
          </FormField>

          <FormField label="Message *" constraintText="Required">
            <Textarea
              value={message}
              onChange={({ detail }) => setMessage(detail.value)}
              placeholder="Please provide your feedback here"
              rows={5}
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    );
  }
);

export default FeedbackModal;
